import React from 'react';
import { useToggle } from 'react-use';

import { ChevronDown, ChevronUp } from '@gravity-ui/icons';
import { CircleQuestion } from '@gravity-ui/icons';
import { Card, Icon, Tooltip } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { Collapsible } from 'client/blocks/Collapsible/Collapsible';
import {
    SMART_HOME_VALIDATION_TYPES,
    SmartHomeValidationResponse,
    SmartHomeValidationStatuses,
    SmartHomeValidationType,
} from 'client/model/draft';
import * as keyset from 'i18n/DraftControlPanel';

import styles from './SmartHomeModeration.module.scss';

const t = i18n(keyset);
const URL_BREAK_CHUNK_LENGTH = 24;

const BreakableUrl = ({ url }: { url: string }) => {
    const chunks = url.match(new RegExp(`.{1,${URL_BREAK_CHUNK_LENGTH}}`, 'g')) ?? [url];

    return (
        <>
            {chunks.map((chunk, index) => (
                <React.Fragment key={`${index}-${chunk}`}>
                    {chunk}
                    {index < chunks.length - 1 && <wbr />}
                </React.Fragment>
            ))}
        </>
    );
};

const SslCheckMessage = () => {
    return (
        <Tooltip content={t('validation-ssl-hint')} openDelay={0}>
            <span>
                {t('validation-ssl')}
                <Icon className={styles.validationStatus__icon} data={CircleQuestion} />
            </span>
        </Tooltip>
    );
};

const SmartHomeValidationTypeText: Record<SmartHomeValidationType, string> = {
    availability: t('validation-availability'),
    user_unlink: t('validation-user_unlink'),
    devices: t('validation-devices'),
    devices_action: t('validation-devices_action'),
    devices_query: t('validation-devices_query'),
};

const SmartHomeValidationStatusText: Record<SmartHomeValidationResponse['status'], React.ReactNode> = {
    OK: '',
    TIMEOUT: t('validation-timeout'),
    NOT_FOUND: t('validation-not_found'),
    PROVIDER_INTERNAL_ERROR: t('validation-internal-error'),
    SSL_CHECK_FAILED: <SslCheckMessage />,
};

type ValidationStatusItemProps = {
    validationType: SmartHomeValidationType;
    response: SmartHomeValidationResponse;
    url: string;
};
const ValidationStatusesItem = ({
    response: { http_code, http_method, status },
    validationType,
    url,
}: ValidationStatusItemProps) => {
    const [opened, toggle] = useToggle(false);
    return (
        <Card className={styles.validationStatus__item} theme="danger" view="filled" qa="smart-home-validation-status">
            <div
                className={styles.validationStatus__toggle}
                data-qa="smart-home-validation-status-toggle"
                onClick={toggle}
            >
                <Icon data={opened ? ChevronUp : ChevronDown} />
            </div>
            <div>
                <div className={styles.validationStatus__title}>{t('validation-status')}</div>
                <div className={styles.validationStatus__value}>{SmartHomeValidationTypeText[validationType]}</div>
            </div>
            <Collapsible visible={opened}>
                <div className={styles.validationStatus__cut}>
                    <div>
                        <div className={styles.validationStatus__title}>{t('validation-error_type')}</div>
                        <div className={styles.validationStatus__value}>{SmartHomeValidationStatusText[status]}</div>
                    </div>
                    <div>
                        <div className={styles.validationStatus__title}>URL</div>
                        <div
                            className={clx(styles.validationStatus__value, styles.validationStatus__url)}
                            data-qa="smart-home-validation-status-url"
                        >
                            <BreakableUrl url={url} />
                        </div>
                    </div>
                    <div>
                        <div className={styles.validationStatus__title}>{t('validation-method')}</div>
                        <div className={styles.validationStatus__value}>{http_method}</div>
                    </div>
                    <div>
                        <div className={styles.validationStatus__title}>{t('validation-code')}</div>
                        <div className={styles.validationStatus__value}>{http_code || <span>&mdash;</span>}</div>
                    </div>
                </div>
            </Collapsible>
        </Card>
    );
};

type ValidationStatusesProps = {
    statuses: SmartHomeValidationStatuses;
};
export const SmartHomeValidationStatusesList = ({ statuses }: ValidationStatusesProps) => {
    return (
        <div>
            {SMART_HOME_VALIDATION_TYPES.map((k) => {
                const response = statuses[k];

                return (
                    response &&
                    response.status !== 'OK' && (
                        <ValidationStatusesItem
                            key={String(k)}
                            url={response.url}
                            validationType={k}
                            response={response}
                        />
                    )
                );
            })}
        </div>
    );
};
